import { formatNumber, Select } from "@";

export default function Pagination({
  localPg = false,
  totalRows = 0,
  currentPage = 1,
  totalPages = 10,
  rowsPerPage = 10,
  onPageChange = () => {},
  onRowsPerPageChange = () => {},
  rowsPerPageOptions = [5, 10, 25, 50, 100],
}) {
  if (totalRows <= 4) {
    return null;
  }

  const pageNumbers = [];
  const maxVisiblePages = 5;

  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  const getVisiblePages = () => {
    if (totalPages <= maxVisiblePages) return pageNumbers;

    const halfVisible = Math.floor(maxVisiblePages / 2);
    let start = currentPage - halfVisible;
    let end = currentPage + halfVisible;

    if (start < 1) {
      end += Math.abs(start) + 1;
      start = 1;
    }

    if (end > totalPages) {
      start -= end - totalPages;
      end = totalPages;
    }

    const visiblePages = pageNumbers.slice(start - 1, end);

    if (start > 1) visiblePages.unshift("...");
    if (end < totalPages) visiblePages.push("...");

    return visiblePages;
  };

  const handlePageChange = (page) => {
    onPageChange(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleRowsPerPageChange = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    onRowsPerPageChange(newRowsPerPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const rowsPerPageItems = rowsPerPageOptions.map((option) => ({
    value: option === "all" ? totalRows : option,
    label: option === "all" ? "الكل" : String(option),
  }));
  const currentRowsPerPageOption =
    rowsPerPageItems.find((o) => o.value === rowsPerPage) ?? null;
  const triggerRowsPerPageChange = (opt) => {
    if (!opt) return;
    handleRowsPerPageChange({ target: { value: String(opt.value) } });
  };

  // Reusable arrow button class
  const navBtnClass =
    "group/nav inline-flex h-9 items-center gap-1.5 rounded-full border border-gray-100 bg-white px-3 text-sm font-medium text-gray-600 transition-all duration-200 hover:border-purple-200 hover:bg-purple-50 hover:text-purple-900 active:scale-95 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-gray-100 disabled:hover:bg-white disabled:hover:text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-purple-700 dark:hover:bg-gray-700 dark:hover:text-purple-300 dark:disabled:hover:border-gray-700 dark:disabled:hover:bg-gray-800 dark:disabled:hover:text-gray-300";

  return (
    <>
      <div className="relative flex items-center justify-between gap-3 rounded-b-2xl border-t border-gray-100 bg-gradient-to-r from-white via-white to-purple-50/20 px-4 py-3.5 dark:border-gray-700/60 dark:from-gray-800 dark:via-gray-800 dark:to-purple-950/10 sm:px-6">
        {/* Mobile view */}
        <div className="flex flex-1 items-center justify-between gap-3 lg:hidden">
          <button
            type="button"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={navBtnClass}
          >
            <svg
              className="h-4 w-4 transition-transform group-hover/nav:translate-x-0.5"
              fill="none"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2.2"
                d="M9 5l7 7-7 7"
              />
            </svg>
            السابق
          </button>

          <div className="w-fit rows-per-page-select">
            <Select
              inputId="rowsPerPageSm"
              value={currentRowsPerPageOption}
              options={rowsPerPageItems}
              onChange={triggerRowsPerPageChange}
            />
          </div>

          <button
            type="button"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={navBtnClass}
          >
            التالي
            <svg
              className="h-4 w-4 transition-transform group-hover/nav:-translate-x-0.5"
              fill="none"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2.2"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
        </div>

        {/* Desktop view */}
        <div className="hidden lg:flex lg:flex-1 lg:items-center lg:justify-between">
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              يعرض
              <span className="mx-1 font-bold tabular-nums text-gray-900 dark:text-gray-100">
                {formatNumber((currentPage - 1) * rowsPerPage + 1)}
              </span>
              إلى
              <span className="mx-1 font-bold tabular-nums text-gray-900 dark:text-gray-100">
                {formatNumber(Math.min(currentPage * rowsPerPage, totalRows))}
              </span>
              من أصل
              <span className="mx-1 font-bold tabular-nums text-purple-700 dark:text-purple-300">
                {formatNumber(totalRows)}
              </span>
              سجل
            </p>
          </div>

          {localPg && (
            <div className="inline-flex items-center gap-2">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                أظهر
              </span>
              <div className="w-[91px]">
                <Select
                  inputId="rowsPerPage"
                  value={currentRowsPerPageOption}
                  options={rowsPerPageItems}
                  onChange={triggerRowsPerPageChange}
                  isSearchable={false}
                />
              </div>
            </div>
          )}

          <nav
            className="inline-flex items-center gap-3"
            aria-label="Pagination"
          >
            {/* Prev */}
            <button
              type="button"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              aria-label="السابق"
              className="group/nav inline-flex h-9 w-9 items-center justify-center rounded-xl border border-gray-100 bg-white text-gray-500 transition-all duration-200 hover:border-purple-200 hover:bg-purple-50 hover:text-purple-900 active:scale-95 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-gray-100 disabled:hover:bg-white disabled:hover:text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:border-purple-700 dark:hover:bg-gray-700 dark:hover:text-purple-300 dark:disabled:hover:border-gray-700 dark:disabled:hover:bg-gray-800 dark:disabled:hover:text-gray-400"
            >
              <svg
                className="h-4 w-4 transition-transform group-hover/nav:-translate-x-0.5 rtl:rotate-180"
                fill="none"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2.2"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            {getVisiblePages().map((page, index) => {
              const isActive = page === currentPage;
              const isEllipsis = page === "...";
              return (
                <button
                  type="button"
                  key={index}
                  onClick={() =>
                    typeof page === "number" && handlePageChange(page)
                  }
                  disabled={isEllipsis}
                  aria-current={isActive ? "page" : undefined}
                  className={`relative inline-flex h-9 min-w-[36px] items-center justify-center rounded-xl px-3 text-sm font-semibold tabular-nums transition-all duration-200 ${
                    isActive
                      ? "bg-gradient-to-br from-purple-800 to-purple-950 text-white shadow-md shadow-purple-900/25 ring-1 ring-purple-900/10 dark:from-purple-600 dark:to-purple-800"
                      : isEllipsis
                        ? "cursor-default text-gray-300 dark:text-gray-600"
                        : "border border-gray-100 bg-white text-gray-600 hover:border-purple-200 hover:bg-purple-50 hover:text-purple-900 active:scale-95 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-purple-700 dark:hover:bg-gray-700 dark:hover:text-purple-300"
                  }`}
                >
                  {page}
                </button>
              );
            })}

            {/* Next */}
            <button
              type="button"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              aria-label="التالي"
              className="group/nav inline-flex h-9 w-9 items-center justify-center rounded-xl border border-gray-100 bg-white text-gray-500 transition-all duration-200 hover:border-purple-200 hover:bg-purple-50 hover:text-purple-900 active:scale-95 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-gray-100 disabled:hover:bg-white disabled:hover:text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:border-purple-700 dark:hover:bg-gray-700 dark:hover:text-purple-300 dark:disabled:hover:border-gray-700 dark:disabled:hover:bg-gray-800 dark:disabled:hover:text-gray-400"
            >
              <svg
                className="h-4 w-4 transition-transform group-hover/nav:translate-x-0.5 rtl:rotate-180"
                fill="none"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2.2"
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </nav>
        </div>
      </div>

      {/* Mobile summary */}
      <div className="block pb-4 pt-2 text-center lg:hidden">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          يعرض
          <span className="mx-1 font-bold tabular-nums text-gray-900 dark:text-gray-100">
            {formatNumber((currentPage - 1) * rowsPerPage + 1)}
          </span>
          إلى
          <span className="mx-1 font-bold tabular-nums text-gray-900 dark:text-gray-100">
            {formatNumber(Math.min(currentPage * rowsPerPage, totalRows))}
          </span>
          من أصل
          <span className="mx-1 font-bold tabular-nums text-purple-700 dark:text-purple-300">
            {formatNumber(totalRows)}
          </span>
          سجل
        </p>
      </div>
    </>
  );
}
